import { APPLICATION_CONFIG, AUTH_API_ROUTES } from "@constants/constants";
import { partnerIdInstance } from "@utils/partner-id-instance";
import { tokenInstance } from "@utils/token-instance";
import axios from "axios";

import { attachAuthInterceptors } from "./session-refresh";

/**
 * Client for the Auth Gateway — users, roles, partners and audit live there,
 * in a service of its own next to the scoring backend. Both accept the same
 * token, so only the base URL differs.
 */
const instance = axios.create({ baseURL: APPLICATION_CONFIG.auth_url });

const PUBLIC_ROUTES: string[] = [
	AUTH_API_ROUTES.LOGIN,
	AUTH_API_ROUTES.LOGIN_LDAP,
	AUTH_API_ROUTES.REFRESH,
];

// The temporary password is changed with the temporary credentials themselves,
// before there is a session to attach or a partner to work in.
const TEMP_PASSWORD_ROUTE = /^\/user\/[^/]+\/change$/;

const isPublicRoute = (url: string | undefined) =>
	PUBLIC_ROUTES.includes(url ?? "") || TEMP_PASSWORD_ROUTE.test(url ?? "");

// Users, roles and partners are all addressed within a partner, named rather
// than identified — the gateway requires it on nearly every one of them. A call
// site that scopes itself differently passes its own `partner` and wins the
// merge.
instance.interceptors.request.use((config) => {
	const partnerName = partnerIdInstance.getPartnerName();

	if (partnerName && !isPublicRoute(config.url)) {
		config.params = { partner: partnerName, ...config.params };
	}

	return config;
});

attachAuthInterceptors(instance, {
	isPublic: (config) => isPublicRoute(config.url),
});

/**
 * Drops the session the gateway holds for this token — the stored one unless
 * told otherwise. Bare on purpose: signing out must not go through the 401
 * handling that tries to restore a session.
 */
export const endGatewaySession = async (
	token: string | null = tokenInstance.getToken(),
): Promise<void> => {
	if (!token) return;

	try {
		await axios.get(`${APPLICATION_CONFIG.auth_url}${AUTH_API_ROUTES.LOGOUT}`, {
			headers: { Authorization: `Bearer ${token}` },
		});
	} catch {
		// An already-dead session is the outcome we wanted anyway.
	}
};

export const authAxiosInstance = instance;

export const authRequest = {
	get: instance.get,
	post: instance.post,
	put: instance.put,
	patch: instance.patch,
	delete: instance.delete,
};
